































































































































































































































import {
  computed,
  defineComponent,
  defineVue3AsyncComponent,
  ref,
  watch
} from '~/utils/nuxt3-migration'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import CInternationalTelephoneInput from '~/components/shared/configurable/form/input/CInternationalTelephoneInput.vue'
import COverlay from '~/components/shared/configurable/overlay/COverlay.vue'
import CRecaptchaText from '~/components/shared/configurable/CRecaptchaText.vue'
import CTextArea from '~/components/shared/configurable/form/input/CTextArea.vue'
import CInputGroup from '~/components/shared/configurable/form/input/CInputGroup.vue'
import CFormGroup from '~/components/shared/configurable/form/CFormGroup.vue'
import CInputGroupText from '~/components/shared/configurable/form/input/CInputGroupText.vue'
import { ciPaperPlane } from '~/icons/source/solid/paper-plane'
import { ciQuestionCircle } from '~/icons/source/regular/question-circle'
import { ciPencil } from '~/icons/source/regular/pencil'
import { useContactForm } from '~/compositions/form/contact'
import { useNamespacedStore } from '~/compositions/store'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { CategoryId } from '~/models/category/types'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import {
  CLASSIFIED_SEARCH_NS,
  SearchState
} from '~/store/modules/shared/classifieds/search/state'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'
import {
  SET_SHOW_CONTACT,
  SET_SHOW_CONTACT_PROMPT
} from '~/store/modules/shared/classifieds/view/mutation-types'

export default defineComponent({
  components: {
    COverlay,
    CFormGroup,
    CFormError,
    CInternationalTelephoneInput,
    CRecaptchaText,
    CTextArea,
    CInputGroup,
    CInputGroupText,
    TradeVehicleInMessages: defineVue3AsyncComponent(() =>
      import('~/components/car/classifieds/TradeVehicleInMessages.vue')
    ),
    MiniAttachmentUploader: defineVue3AsyncComponent(() =>
      import(
        '~/components/car/classifieds/view/partials/MiniAttachmentUploader.vue'
      )
    )
  },
  props: {
    classifiedId: { type: Number, default: null },
    siteUrl: { type: String, required: false, default: 'car.gr' },
    buttonText: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const {
      getters: classifiedGetters,
      state: classifiedState,
      commit
    } = useNamespacedStore<ClassifiedViewState>(CLASSIFIED_VIEW_NS)
    const { getters: classifiedSearchGetters } = useNamespacedStore<
      SearchState
    >(CLASSIFIED_SEARCH_NS)
    const { t, tc } = useI18n()

    const {
      canTradeInMessage,
      hasMessagesForClassified,
      messageStats,
      values,
      schema,
      errors,
      isReady,
      isLoading,
      isSuccess,
      threadToGo,
      isManager,
      managerOptions,
      hasRecipients,
      recipientsOptions,
      recaptchaWidgetContainerId,
      recaptchaFallbackWidgetLoaded,
      senderChange,
      submitContactForm
    } = useContactForm({
      formUrl: `/api/classifieds/${props.classifiedId}/contact/`
    })

    const uploading = ref(false)

    const isAdmin = computed(() => userGetters('isAdmin'))
    const isAnon = computed(() => userGetters('isAnon'))
    const isSingle = computed(() => userGetters('isSingle'))
    const categoryIds = computed(() => classifiedState.classified?.category_ids)
    const isJobOffered = computed(() => classifiedGetters('isJobOffered'))
    const isOwn = computed(() => classifiedGetters('isOwn'))
    const isWantedParts = computed(
      () =>
        classifiedGetters('isWantedPartsClassified') ||
        classifiedSearchGetters('isWantedPartsSearch')
    )
    const isCarCategory = computed(() => {
      return categoryIds.value?.includes(CategoryId.CARS)
    })
    const isBikeCategory = computed(() => {
      return categoryIds.value?.includes(CategoryId.BIKES)
    })
    const classifiedType = computed(() => {
      if (isCarCategory.value) {
        return 'cars'
      } else if (isBikeCategory.value) {
        return 'bikes'
      }
      return ''
    })
    const classifiedMine = computed(() => {
      // if we are dispatched
      if (
        process.client &&
        window.frameElement &&
        window.frameElement.getAttribute('clsfd-mine')
      ) {
        return window.frameElement.getAttribute('clsfd-mine') === 'True'
      }

      // if we are in car2
      if (isOwn.value) {
        return true
      }

      return false
    })
    const messageTemplate = computed(() => {
      return t(
        isWantedParts.value
          ? 'wanted_parts_message_template'
          : 'message_template',
        {
          siteUrl: props.siteUrl,
          classifiedId: props.classifiedId
        }
      )
    })
    const responseTime = computed(() => {
      if (messageStats.value?.replyTimeMedian) {
        const minutes = Math.round(messageStats.value.replyTimeMedian)

        const years = Math.floor(minutes / 525600) // 1 year = 525600 minutes
        const months = Math.floor((minutes % 525600) / 43800) // 1 month = 43800 minutes
        const weeks = Math.floor(((minutes % 525600) % 43800) / 10080) // 1 week = 10080 minutes
        const days = Math.floor((((minutes % 525600) % 43800) % 10080) / 1440) // 1 day = 1440 minutes
        const hours = Math.floor(
          ((((minutes % 525600) % 43800) % 10080) % 1440) / 60
        )

        if (years > 0)
          return `${years} ${tc('year | years', years).toLowerCase()}`
        if (months > 0)
          return `${months} ${tc('month | months', months).toLowerCase()}`
        if (weeks > 0)
          return `${weeks} ${tc('week | weeks', weeks).toLowerCase()}`
        if (days > 0) return `${days} ${tc('day | days', days).toLowerCase()}`
        if (hours > 0)
          return `${hours} ${tc('hour | hours', hours).toLowerCase()}`
        if (minutes > 0)
          return `${minutes} ${tc('minute | minutes', minutes).toLowerCase()}`
      }
      return null
    })
    const isResponseTimeVisible = computed(() => {
      const replyTimeMinutes = messageStats.value?.replyTimeMedian

      if (!replyTimeMinutes) {
        return false
      }

      const replyTimeDays = Math.floor(replyTimeMinutes / 60 / 24)

      return isAdmin.value || replyTimeDays <= 4
    })

    watch(isReady, newValue => {
      if (newValue) {
        if (schema.value === null) {
          // something weird happened, so hide the form
          if (isSingle.value && isOwn.value && hasMessagesForClassified.value) {
            commit(SET_SHOW_CONTACT, true)
            emit('what-to-show', 'messages-only')
          } else {
            commit(SET_SHOW_CONTACT, false)
          }

          if (
            isAnon.value ||
            (isSingle.value && !isOwn.value && !hasMessagesForClassified.value)
          ) {
            commit(SET_SHOW_CONTACT, true)
          }
          return
        } else {
          commit(SET_SHOW_CONTACT, true)
          commit(SET_SHOW_CONTACT_PROMPT, false)
        }

        if (hasMessagesForClassified.value) {
          if (isAdmin.value) {
            emit('what-to-show', 'both')
          } else {
            emit('what-to-show', 'messages-only')
          }
        } else {
          emit('what-to-show', 'contact-only')
        }

        values.value.body = messageTemplate.value
      }
    })

    function resumeInput(resumeArray) {
      for (let i = 0; i < resumeArray.length; i++) {
        resumeArray[i].tags = ['resume']
      }
      values.value.attachments = resumeArray
    }

    return {
      ciPaperPlane,
      ciQuestionCircle,
      ciPencil,
      values,
      schema,
      errors,
      isReady,
      isLoading,
      isSuccess,
      threadToGo,
      isManager,
      managerOptions,
      hasRecipients,
      recipientsOptions,
      recaptchaWidgetContainerId,
      recaptchaFallbackWidgetLoaded,
      canTradeInMessage,
      uploading,
      classifiedType,
      messageTemplate,
      responseTime,
      isResponseTimeVisible,
      isJobOffered,
      classifiedMine,
      isAnon,
      senderChange,
      submitContactForm,
      resumeInput
    }
  },
  i18n: defineComponentTranslations({
    message_template: {
      en:
        'I am interested in your {siteUrl} listing,\nwith classified number {siteUrl}/{classifiedId}\nPlease contact me.\n\n\n Best regards',
      el:
        'Ενδιαφέρομαι για την αγγελία σας,\nμε αριθμό {siteUrl}/{classifiedId}\nΠαρακαλώ επικοινωνήστε μαζί μου.\n\n\n Με εκτίμηση'
    },
    wanted_parts_message_template: {
      en:
        "I have the spare part you're looking for in the classified\nwith number {siteUrl}/{classifiedId}\nPlease contact me.\n\n\n Best regards",
      el:
        'Διαθέτω το ανταλλακτικό που ζητάτε στην αγγελία\nμε αριθμό {siteUrl}/{classifiedId}\nΠαρακαλώ επικοινωνήστε μαζί μου.\n\n\n Με εκτίμηση'
    }
  })
})
